<template>
  <div class="login">
    <div class="form-container">
      <h1 class="welcome">欢迎使用</h1>
      <p class="subtitle">登录以继续使用</p>

      <van-form @submit="onSubmit" class="form">
        <div class="input-group">
          <van-field
            v-model="username"
            name="username"
            placeholder="用户名"
            :rules="[{ required: true, message: '请填写用户名' }]"
            class="custom-field"
          />
          <van-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            placeholder="密码"
            :rules="[{ required: true, message: '请填写密码' }]"
            class="custom-field"
          >
            <template #right-icon>
              <van-icon 
                :name="showPassword ? 'eye-o' : 'closed-eye'" 
                @click="showPassword = !showPassword"
                class="eye-icon"
              />
            </template>
          </van-field>
        </div>
        
        <div class="submit-btn">
          <van-button
            block
            type="primary"
            native-type="submit"
            :loading="loading"
            loading-text="登录中..."
          >
            登录
          </van-button>
        </div>
      </van-form>

      <div class="divider">
        <span class="divider-text">或</span>
      </div>

      <div class="actions">
        <van-button block plain @click="goToRegister" class="register-btn">
          创建新账号
        </van-button>
        <a class="forgot-link" @click="showResetPassword">忘记密码？</a>
      </div>
    </div>

    <van-dialog
      v-model:show="showResetDialog"
      title="重置密码"
      show-cancel-button
      @confirm="resetPassword"
      class="reset-dialog"
    >
      <van-field
        v-model="resetEmail"
        placeholder="请输入注册邮箱"
        :rules="[
          { required: true, message: '请填写邮箱' },
          { pattern: /.+@.+\..+/, message: '请输入正确的邮箱格式' }
        ]"
      />
    </van-dialog>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import AV from 'leancloud-storage'
import { showNotify } from 'vant'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const username = ref('')
    const password = ref('')
    const showPassword = ref(false)
    const showResetDialog = ref(false)
    const resetEmail = ref('')
    const loading = ref(false)

    const onSubmit = async () => {
      loading.value = true
      try {
        await AV.User.logIn(username.value, password.value)
        showNotify({ 
          type: 'success', 
          message: '登录成功',
          duration: 1000
        })
        router.push('/')
      } catch (error) {
        showNotify({ 
          type: 'danger', 
          message: '登录失败：' + error.message 
        })
      } finally {
        loading.value = false
      }
    }

    const goToRegister = () => {
      router.push('/register')
    }

    const showResetPassword = () => {
      showResetDialog.value = true
    }

    const resetPassword = async () => {
      if (!resetEmail.value) {
        showNotify({ type: 'warning', message: '请输入邮箱' })
        return
      }
      
      try {
        await AV.User.requestPasswordReset(resetEmail.value)
        showNotify({ 
          type: 'success', 
          message: '重置密码邮件已发送' 
        })
        resetEmail.value = ''
        showResetDialog.value = false
      } catch (error) {
        showNotify({ 
          type: 'danger', 
          message: '发送失败：' + error.message 
        })
      }
    }

    return {
      username,
      password,
      showPassword,
      showResetDialog,
      resetEmail,
      loading,
      onSubmit,
      goToRegister,
      showResetPassword,
      resetPassword
    }
  }
}
</script>

<style scoped>
.login {
  min-height: 100vh;
  height: 100vh;
  background-color: #fff;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0px 0 0px 0;
  min-height: min-content;
}

.welcome {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #000;
}

.subtitle {
  font-size: 16px;
  color: #86868b;
  margin: 0 0 40px;
}

.input-group {
  background: #f5f5f7;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.custom-field {
  background: transparent;
}

:deep(.van-field) {
  padding: 14px 16px;
}

:deep(.van-field__control) {
  font-size: 16px;
}

:deep(.van-field__control::placeholder) {
  color: #86868b;
}

.eye-icon {
  color: #86868b;
  font-size: 20px;
}

.submit-btn {
  margin: 24px 0;
}

:deep(.van-button--primary) {
  background: #0071e3;
  border: none;
  height: 50px;
  font-size: 17px;
  font-weight: 400;
  border-radius: 12px;
}

:deep(.van-button--primary:active) {
  background: #0077ed;
}

.divider {
  position: relative;
  text-align: center;
  margin: 32px 0;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: #d2d2d7;
}

.divider-text {
  position: relative;
  background: #fff;
  padding: 0 16px;
  color: #86868b;
  font-size: 14px;
}

.register-btn {
  height: 50px;
  font-size: 17px;
  border-radius: 12px;
  border: 1px solid #d2d2d7;
  color: #000;
}

:deep(.register-btn:active) {
  background: #f5f5f7;
}

.forgot-link {
  display: block;
  text-align: center;
  margin-top: 16px;
  color: #0071e3;
  font-size: 14px;
  text-decoration: none;
}

.reset-dialog {
  :deep(.van-dialog__header) {
    padding: 24px 0 0;
    font-weight: 600;
  }
  
  :deep(.van-dialog__content) {
    padding: 24px;
  }
  
  :deep(.van-button) {
    height: 44px;
    font-size: 16px;
  }
}

@media screen and (max-height: 667px) {
  .form-container {
    padding: 20px 0;
  }

  .welcome {
    font-size: 28px;
    margin: 0 0 4px;
  }

  .subtitle {
    font-size: 15px;
    margin: 0 0 24px;
  }

  .submit-btn {
    margin: 16px 0;
  }

  .divider {
    margin: 24px 0;
  }
}
</style> 